import React, { useEffect, useState } from 'react';
import LMSHeader from './LMSHeader';
import { NavLink } from 'react-router-dom';
import useAuthStore from '../stores/useAuthStore';
import { useToast } from './ToastContainer';


const DashboardLayout = ({ children, userRole }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const { logout } = useAuthStore();
  const toast = useToast();

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  useEffect(() => {
    const isMobile = window.innerWidth < 768;
    setIsSidebarOpen(!isMobile);
  }, []);


  const handleLogout = async () => {
    try {
      await logout();
      toast.success('You’ve been logged out successfully 👋');
    } catch (error) {
      toast.error('Logout failed. Please try again.');
    }
  };

  const sidebarItems = {
    student: [
      { label: 'Dashboard', icon: '📊', path: '/student/dashboard' },
      { label: 'My Courses', icon: '🎓', path: '/student/my-courses' },
      { label: 'Progress', icon: '📈', path: '/student/progress' },
      { label: 'Achivements', icon: '📅', path: '/student/achivements' },
      { label: 'Wishlist', icon: '💬', path: '/student/wishlist' },
      { label: 'Notifications', icon: '🔔', path: '/student/notifications' },
      { label: 'Billings', icon: '📅', path: '/student/billings' },
      { label: 'Profile', icon: '👥', path: '/student/profile' },

    ],
    instructor: [
      { label: 'Dashboard', icon: '📊', path: '/instructor/dashboard' },
      { label: 'Course Builder', icon: '🎓', path: '/instructor/course/builder' },
      { label: 'My Courses', icon: '📚', path: '/instructor/my-courses' },
      { label: 'Discussions', icon: '💰', path: '/instructor/discussions' },
      { label: 'Analytics', icon: '📈', path: '/instructor/analytics' },
      { label: 'Certificates', icon: '🏅', path: '/instructor/certificates' },
      { label: 'Revenue', icon: '💰', path: '/instructor/revenue' },
      { label: 'Assignment', icon: '📝', path: '/instructor/assignment' },
      { label: 'Notifications', icon: '🔔', path: '/instructor/notifications' },
      { label: 'Coupons', icon: '🎟️', path: '/instructor/coupons' },
      { label: 'Profile', icon: '👥', path: '/instructor/profile' },
    ],
    admin: [
      { label: 'Dashboard', icon: '📊', path: '/admin/cloud/dashboard' },
      { label: 'Users', icon: '👥', path: '/admin/cloud/users' },
      { label: 'Course Builder', icon: '🎓', path: '/admin/cloud/course/builder' },
      { label: 'Courses', icon: '🎓', path: '/admin/cloud/courses' },
      { label: 'Certificates', icon: '🏅', path: '/admin/cloud/certificates' },
      { label: 'Categories', icon: '📁', path: '/admin/cloud/categories' },
      { label: 'Withdrawals', icon: '💰', path: '/admin/cloud/withdrawal' },
      { label: 'Analytics', icon: '📑', path: '/admin/cloud/analytics' },
      { label: 'Partners', icon: '🤝', path: '/admin/cloud/partners' },
      { label: 'Organizations', icon: '🏢', path: '/admin/cloud/organizations' },
      { label: 'Schools', icon: '🎓', path: '/admin/cloud/schools' },
      { label: 'Coupons', icon: '🎟️', path: '/admin/cloud/coupons' },
      { label: 'Notifications', icon: '🔔', path: '/admin/cloud/notifications' },
      { label: 'Settings', icon: '⚙️', path: '/admin/cloud/settings' },
    ],
  };

  const items = sidebarItems[userRole] || sidebarItems.student;

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex flex-col">
      <LMSHeader />
      <div className="flex flex-1">
        {/* SIDEBAR */}
        <aside
          className={`${isSidebarOpen ? 'w-64' : 'w-0'
            } md:w-64 bg-white dark:bg-neutral-800 border-r border-neutral-200 dark:border-neutral-700 transition-all duration-300 overflow-hidden flex flex-col fixed md:static inset-y-0 left-0 z-50`}
        >
          <button
            onClick={toggleSidebar}
            className="absolute top-4 right-4 md:hidden bg-white/80 dark:bg-neutral-200/80 p-2 rounded-full shadow-lg z-50"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
            {items.map((item, index) => (
              <NavLink
                key={index}
                to={item.path}
                className={({ isActive }) =>
                  `flex items-center px-3 py-3 text-sm font-medium rounded-lg transition-colors group ${isActive
                    ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-400'
                    : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700'}`
                }
              >
                <span className="mr-3 text-lg group-hover:scale-110 transition-transform">
                  {item.icon}
                </span>
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>
          <div className="p-4 border-t border-neutral-200 dark:border-neutral-700">
            <button onClick={handleLogout} className="w-full text-left px-3 py-2 text-sm text-danger-600 dark:text-danger-400 hover:bg-danger-50 dark:hover:bg-danger-900/20 rounded-lg transition-colors">
              Sign Out
            </button>
          </div>
        </aside>

        {isSidebarOpen && (
          <div
            onClick={toggleSidebar}
            className="fixed inset-0 bg-black/40 md:hidden z-40"
          ></div>
        )}

        {/* MAIN CONTENT */}
        <main className="flex-1 p-0 md:p-8">
          {/* Mobile Toggle */}
          <button
            onClick={toggleSidebar}
            className="md:hidden mb-6 flex items-center gap-2 px-3 py-2 
             text-neutral-600 dark:text-neutral-400 
             hover:bg-neutral-200 dark:hover:bg-neutral-700 
             rounded-lg transition"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>

            <span className="text-sm font-medium">
              Dashboard Menu
            </span>
          </button>

          {children}
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;